import { useState } from 'react';
import { usePosse } from '@/leads/usePosse';
import { ehGestor, mostrarTextoDaPosse } from '@/leads/posseTela';
import { assumirLeadParaMim, atribuirSdr } from '@/services/leadsService';

/**
 * ★★★ PC-6 P6 — "SDR responsável" na ficha.
 *
 * - **SDR**: SEM select. O nome de quem tem o lead, o texto da situação (`situacaoDePosse`) e o botão
 *   "Assumir lead" — habilitado só quando `podeAssumir`. Quem decide é a callable `assumirLead`:
 *   se o banco mudou desde que a tela desenhou, a recusa volta com o texto da trava.
 * - **Gestor**: o select continua, e grava pela MESMA callable (as rules fecham a escrita direta para
 *   todos). Por cima da trava (D5), pede confirmação com o motivo — fica na trilha.
 * - **Clínica com 1 SDR** (dono, 24/10): o texto de reserva/trava some; o comportamento continua.
 *
 * `opcoes` são as SDRs elegíveis que a ficha já calcula (`apenasSdrs` + o gravado).
 */
export default function BlocoPosse({ lead, clinicaId, papel = null, usuarioId = null, equipe = [], opcoes = [], disabled = false, rotuloExtra = null }) {
  const situacao = usePosse({ lead, clinicaId, papel, usuarioId });
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState(null);
  const gestor = ehGestor(papel);
  const texto = mostrarTextoDaPosse(situacao, equipe) ? situacao.texto : '';

  async function executar(acao) {
    setErro(null);
    setOcupado(true);
    try {
      await acao();
    } catch (e) {
      setErro(e?.message ?? 'Não foi possível trocar o responsável.');
    } finally {
      setOcupado(false);
    }
  }

  function trocarPeloGestor(sdrId) {
    if ((lead.sdrId ?? '') === sdrId) return;
    if (situacao?.porCimaDaTrava) {
      const ok = window.confirm(`Por cima da trava: ${situacao.texto}\n\nTransferir mesmo assim? A troca fica registrada na timeline.`);
      if (!ok) return;
    }
    const sdr = opcoes.find((u) => u.id === sdrId) ?? null;
    executar(() => atribuirSdr(clinicaId, lead, sdr));
  }

  return (
    <div className="campo" data-bloco="posse">
      <label htmlFor={gestor ? 'pl-sdr' : undefined}>
        SDR responsável
        {rotuloExtra}
      </label>
      {gestor ? (
        <select id="pl-sdr" value={lead.sdrId ?? ''} disabled={disabled || ocupado} onChange={(e) => trocarPeloGestor(e.target.value)}>
          <option value="">Sem responsável</option>
          {opcoes.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nome}
            </option>
          ))}
        </select>
      ) : (
        <div className="posse-sdr">
          <strong data-campo="dona">{lead.sdrNome || (lead.sdrId ? 'SDR' : 'Sem responsável')}</strong>
          {situacao && situacao.estado !== 'seu' && (
            <button
              type="button"
              className="btn-secundario"
              data-acao="assumir-lead"
              disabled={disabled || ocupado || !situacao.podeAssumir}
              onClick={() => executar(() => assumirLeadParaMim(clinicaId, lead))}
            >
              Assumir lead
            </button>
          )}
        </div>
      )}
      {texto && <p className="ajuda" data-campo="situacao-posse">{texto}</p>}
      {erro && <p className="erro" role="alert">{erro}</p>}
    </div>
  );
}
